import React, { useEffect } from 'react';
import { ETableStoragePaths } from 'components/Base/Table/Table.types';
import useTable from 'components/Base/Table/hooks/useTable';
import { useGetTasksQuery } from 'redux/apiSlice/pillarsActionPlan.slice';
import { TTask } from 'data/types/pillarsActionPlan.types';
import EmptyTableState from 'components/Shared/EmptyTableState/EmptyTableState';
import { PlusOutlined } from '@ant-design/icons';
import Button from 'components/Base/Button/Button';
import { useGetColumnsData } from './TaskTable.data';
import { StyledTaskTable } from './TaskTable.style';
import { useTaskAssigneeFilters } from './hooks/useTaskAssigneeFilters';

interface TasksTableProps {
  tasks: TTask[];
  actionPlanId: number;
  onTaskSaved?: (tempRowId: string, newTask: TTask) => void;
  onTaskCancel?: (rowId: string) => void;
  onAddEmptyTask?: () => void; // Add this new prop
  isPreviousPlan?: boolean;
  isAllElcs?: boolean;
}

const TaskTable: React.FC<TasksTableProps> = ({
  tasks,
  actionPlanId,
  onTaskSaved,
  onTaskCancel,
  onAddEmptyTask,
  isPreviousPlan = false,
  isAllElcs = false,
}) => {
  const dataTableName = `tactic_tasks_${actionPlanId}`;

  const getAddTaskTooltip = (): string | undefined => {
    if (isPreviousPlan) {
      return 'This tactic is from a previous plan and cannot be modified';
    }
    if (isAllElcs) {
      return 'Adding tasks is disabled on the global page';
    }
    return undefined;
  };

  const assigneeFilters = useTaskAssigneeFilters({
    actionPlanId,
    tempTasks: tasks?.filter(task => !task.taskId && task.rowId),
  });

  const { defaultColumns } = useGetColumnsData(
    actionPlanId,
    onTaskSaved,
    onTaskCancel,
    assigneeFilters,
    isPreviousPlan,
    isAllElcs,
  );

  const {
    sortColumn,
    selectedFilters,
    hasSelectedFilter,
    onFiltersReset,
    handleTableChange,
    columns,
    setColumns,
  } = useTable({
    storagePath: ETableStoragePaths.TASK,
    defaultColumns,
  });

  const { data, isFetching } = useGetTasksQuery({
    page: 0,
    pageSize: 2000,
    sortColumn,
    filters: selectedFilters,
    actionPlanId,
  });

  const combinedTasks = React.useMemo(() => {
    const apiTasks = data?.content || [];
    const tempTasks = tasks.filter(task => !task.taskId && task.rowId);

    return [...tempTasks, ...apiTasks];
  }, [tasks, data?.content]);

  const memoizedAssigneeFilters = React.useMemo(() => {
    return JSON.stringify(assigneeFilters);
  }, [assigneeFilters]);

  useEffect(() => {
    if (assigneeFilters && defaultColumns) {
      setColumns(defaultColumns);
    }
  }, [memoizedAssigneeFilters]); // ✅ Correct dependencies

  useEffect(() => {
    onFiltersReset();
  }, []);

  return (
    <StyledTaskTable
      key={actionPlanId}
      storagePath={ETableStoragePaths.TASK}
      onFiltersReset={onFiltersReset}
      hasFilters={hasSelectedFilter}
      onChange={handleTableChange}
      columns={columns as any[] | undefined} // Use columns from useTable
      loading={isFetching}
      dataSource={combinedTasks || []}
      pagination={false}
      totalDataCount={data?.totalElements}
      rowKey={record => {
        if (record.taskId) return `task-${record.taskId}`;
        if (record.rowId) return `temp-${record.rowId}`;
        return `fallback-${Math.random()}-${Date.now()}`;
      }}
      data-cy="task-table"
      dataTableName={dataTableName}
      locale={{
        emptyText: () => (
          <EmptyTableState
            message="You do not have tasks yet."
            button={
              <Button
                variant="primary"
                icon={<PlusOutlined />}
                onClick={onAddEmptyTask}
                disabled={isPreviousPlan || isAllElcs}
                tooltip={getAddTaskTooltip()}
              >
                Add Task
              </Button>
            }
          />
        ),
      }}
      hideActions
    />
  );
};

export default TaskTable;
